import { t } from 'i18next';
import { PUSH_IGNORE } from '@/core/flux.service';
import { API_TYPES, ITEM_ICONS, STRING_UOM } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { setEvaluationResults, setTableDefinition } from '@/tableDefinitionEditor/tableDefinition.actions';
import { consolidatedValueFormatter, isItemTableCell } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { setNameInAllTools } from '@/toolSelection/investigate.actions';
import { setTrendItemProps } from '@/trendData/trend.actions';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { Icon } from '@seeqdev/qomponents';
import type { CustomCellRendererProps } from 'ag-grid-react';
import type {
  ColumnDefinition,
  EditorType,
  EvaluationDetailsCellContext,
  ItemTableCell,
  ProcessedTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { EvaluationDetailsModalData } from '@/tableDefinitionEditor/tableDefinitionEditor.store';
import type { IconType } from '@seeqdev/qomponents';

export interface TableDefinitionRendererProps extends CustomCellRendererProps {
  tableDefinitionId: string;
  tableDefinitionColumnDef: ColumnDefinition;
  editorType: EditorType;
  onOpenEvaluationDetails?: (data: EvaluationDetailsModalData) => void;
}

export const TableDefinitionCellRenderer = (props: TableDefinitionRendererProps) => {
  const isGroupRow = props.colDef?.showRowGroup;
  if (isGroupRow) {
    return <>{props.node?.key}</>;
  }

  let valueFormatted = props.valueFormatted;

  let cellData = props.colDef?.field && props.data && props.data[props.colDef.field];
  if (!cellData) {
    // this may be the result of our custom aggregation function
    cellData = props.value;
    valueFormatted = consolidatedValueFormatter({ value: cellData, colDef: props.colDef });
  }
  if (!cellData) {
    return undefined;
  }

  let remove = <></>;
  let info = <></>;
  let modifiedOutsideTable = <></>;

  if (cellData.override) {
    remove = (
      <Icon
        testId="removeIconOverride"
        icon="fa-times"
        large={true}
        extraClassNames="closeIcon ml5"
        onClick={() => deleteOverride(props)}
        tooltip={t('SCALING.OVERRIDE.DELETE_OVERRIDE_TOOLTIP')}
      />
    );
  }

  if (cellData.isModifiedOutsideTable) {
    modifiedOutsideTable = (
      <Icon
        testId="modifiedOutsideTableIcon"
        icon="fa-right-from-bracket"
        large={true}
        extraClassNames="mr5"
        tooltip={t('SCALING.ITEM_MANAGED_OUTSIDE_TABLE')}
      />
    );
    remove = (
      <Icon
        testId="removeIconOutsideTable"
        icon="fa-times"
        large={true}
        extraClassNames="closeIcon ml5"
        onClick={() => deleteManagedOutsideTableProp(props)}
        tooltip={t('SCALING.REMOVE_ITEM_MANAGED_OUTSIDE_TABLE')}
      />
    );
  }

  if (cellData.evaluationDetails) {
    const isEmpty = !cellData.value && !cellData.uuid;
    let iconString = 'fa-info-circle';
    let iconType: IconType = 'theme';
    if (isEmpty) {
      iconString = 'fa-warning';
      iconType = 'warning';
    }
    const rowNodeId = props.node.id;
    const colKey = props.colDef?.field;
    const cellContext: EvaluationDetailsCellContext | undefined =
      rowNodeId !== undefined && colKey !== undefined ? { rowNodeId, colKey, isEmpty } : undefined;
    info = (
      <Icon
        testId="evaluationDetailsIcon"
        icon={iconString}
        large={true}
        type={iconType}
        extraClassNames="mr5"
        onClick={() => {
          if (props.tableDefinitionColumnDef && props.onOpenEvaluationDetails) {
            props.onOpenEvaluationDetails({
              evaluationColumn: props.tableDefinitionColumnDef,
              evaluationResults: cellData.evaluationDetails,
              cellContext,
            });
          }
        }}
        tooltip={t('SCALING.EVALUATION_DETAILS_TOOLTIP')}
      />
    );
  }

  const icon = (cellData: ItemTableCell) => {
    const properties = cellData.fetchedProperties;
    if (!properties) {
      return <></>;
    }

    const valueUom = properties[SeeqNames.Properties.ValueUom];
    const seriesIcon = valueUom === STRING_UOM ? ITEM_ICONS.STRING_SERIES : ITEM_ICONS.SERIES;

    const itemIconMapping = {
      [API_TYPES.ASSET]: ITEM_ICONS.ASSET,
      [API_TYPES.CALCULATED_CONDITION]: ITEM_ICONS.CONDITION,
      [API_TYPES.CALCULATED_SCALAR]: ITEM_ICONS.SCALAR,
      [API_TYPES.CALCULATED_SIGNAL]: seriesIcon,
      [API_TYPES.CAPSULE]: ITEM_ICONS.CAPSULE,
      [API_TYPES.LITERAL_SCALAR]: ITEM_ICONS.SCALAR,
      [API_TYPES.STORED_SIGNAL]: seriesIcon,
    };

    const itemType = properties[SeeqNames.Properties.Type];
    const iconClass = (itemType && itemIconMapping[itemType]) || ITEM_ICONS.ASSET;

    return <Icon icon={iconClass} extraClassNames="mr5" />;
  };

  return (
    <>
      {icon(cellData)}
      {info}
      {modifiedOutsideTable}
      {valueFormatted}
      {remove}
    </>
  );
};

const deleteOverride = async (props: TableDefinitionRendererProps) => {
  if (props.colDef?.field === undefined || props.api === undefined) {
    return;
  }
  const columns = props.api.getColumns();
  const columnIdItemId = columns
    ?.find((c) => c.getColDef().headerName === SeeqNames.MaterializedTables.ItemIdColumn)
    ?.getColId();

  const columnIdDatumId = columns
    ?.find((c) => c.getColDef().headerName === SeeqNames.MaterializedTables.DatumIdColumn)
    ?.getColId();

  const itemId: string = columnIdItemId ? props.data[columnIdItemId].uuid : props.tableDefinitionId;
  const datumId: string = columnIdDatumId ? props.data[columnIdDatumId].value : undefined;

  try {
    const { data: computeData } = await sqTableDefinitionsApi.deleteOverridesFromColumn({
      id: props.tableDefinitionId,
      columnId: props.colDef.field,
      itemId,
      datumId,
    });
    setTableDefinition(props.editorType, computeData.tableDefinition);
    setEvaluationResults(props.editorType, computeData.computeResults);
    successToast({ messageKey: 'SCALING.OVERRIDE.OVERRIDE_DELETE_SUCCESS' });
    props.api.clearFocusedCell();
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

const deleteManagedOutsideTableProp = async (props: TableDefinitionRendererProps) => {
  if (props.colDef?.field === undefined) {
    return;
  }
  try {
    await sqItemsApi.deleteProperty({
      id: props.value.uuid,
      propertyName: SeeqNames.Properties.IsModifiedOutsideTable,
    });
    const { data: computeData } = await sqTableDefinitionsApi.recomputeColumnInTableDefinition(
      { rowIds: [] },
      {
        id: props.tableDefinitionId,
        columnId: props.colDef.field,
        includeDetailedResults: true,
      },
    );
    setTableDefinition(props.editorType, computeData.tableDefinition);
    setEvaluationResults(props.editorType, computeData.computeResults);

    // Update the name in trend item and all tools in case the name was changed
    const { data: propertyData } = await sqItemsApi.getProperty({
      id: props.value.uuid,
      propertyName: SeeqNames.Properties.Name,
    });
    if (propertyData?.value) {
      setNameInAllTools(propertyData.value, props.value.uuid);
      setTrendItemProps(props.value.uuid, { name: propertyData.value }, PUSH_IGNORE);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const AssetBuilderGroupHeader = (params: CustomCellRendererProps) => {
  let value: string | undefined;
  const cell = params.getValue?.() as ProcessedTableCell | undefined;
  if (isItemTableCell(cell)) {
    value = cell.fetchedProperties?.[SeeqNames.Properties.Name];
  } else if (cell) {
    value = consolidatedValueFormatter({ value: cell });
  }
  return <span>{value}</span>;
};
